import {
  Button,
  CustomFilter,
  DatePicker,
  EllipsisSpan,
  FieldTable,
  Form,
  Input,
  Paging,
  PopConfirm,
  Select,
  Tag,
  useBreadcrumbRoutes,
  usePaging,
  useUpdate,
  Wrapper,
} from '@maxtropy/components';
import { Cascader, Space } from 'antd';
import { useRequest } from 'ahooks';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiV2CustomerBillDeletePost, apiV2CustomerBillPagePost } from '@maxtropy/device-customer-apis-v2';
import dayjs from 'dayjs';
import { energyMediumSceneToMediumRes, getEnergyMediumSceneToMedium } from '@/api/workingSubstance';

interface searchParams {
  billName?: string;
  billMonth?: number;
  energyMediumId?: number;
}

const columns = [
  {
    title: '账单名称',
    dataIndex: 'billName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '账单月份',
    dataIndex: 'billMonth',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={dayjs(v).format('YYYY-MM')} />,
  },
  {
    title: '能源介质',
    dataIndex: 'energyMediumName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '账单类型',
    dataIndex: 'billType',
    ellipsis: { showTitle: true },
    render: (v: number) => (
      <EllipsisSpan value={v === 1 ? <Tag color="#cd201f">支出</Tag> : <Tag color="#28b158ff">收入</Tag>} />
    ),
  },
  {
    title: '账单金额',
    dataIndex: 'billAmount',
    ellipsis: { showTitle: true },
    render: (v: number) => (
      <EllipsisSpan
        value={v === null || v === undefined ? '-' : v >= 10000 ? `${(v / 10000).toFixed(2)}万元` : `${v.toFixed(2)}元`}
      />
    ),
  },
  {
    title: '账单用量',
    dataIndex: 'usage',
    ellipsis: { showTitle: true },
    render: (v: string, record: any) => (
      <EllipsisSpan value={v !== undefined && v !== null ? `${v}${record.physicalUnitName || ''}` : '-'} />
    ),
  },
  {
    title: '最后操作时间',
    dataIndex: 'updateTime',
    ellipsis: { showTitle: true },
    render: (v: number) => <EllipsisSpan value={dayjs(v).format('YYYY-MM-DD')} />,
  },
  {
    title: '最后操作人',
    dataIndex: 'updater',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
];

const HistoryBillImport = () => {
  const buildColumns = [
    ...columns,
    {
      title: '操作',
      width: 180,
      render: (record: any) => {
        return (
          <Space size={16}>
            <Button type="link" onClick={() => navigate(`/historybill/bills/import/${record.id}?mode=view`)}>
              查看
            </Button>
            <Button type="link" onClick={() => navigate(`/historybill/bills/import/${record.id}?mode=edit`)}>
              编辑
            </Button>
            <PopConfirm title="!确认删除？删除后不可恢复，你还要继续吗？" onConfirm={() => deleteConfig(record.id)}>
              <Button type="link" danger>
                删除
              </Button>
            </PopConfirm>
          </Space>
        );
      },
    },
  ];
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [update, updateFn] = useUpdate();
  const pagingInfo = usePaging(20);

  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
  const [energyMediumSceneToMedium, setEnergyMediumSceneToMedium] = useState<energyMediumSceneToMediumRes[]>([]);
  const [form] = Form.useForm();
  const [searchParams, setSearchParams] = useState<searchParams>();
  const navigate = useNavigate();

  useEffect(() => {
    getEnergyMediumSceneToMedium().then(setEnergyMediumSceneToMedium);
  }, []);
  const energyMediumSceneToMediumOptions = useMemo(() => {
    return energyMediumSceneToMedium.map(i => ({
      label: i.sceneName,
      value: i.id,
      children: i.energyMediums.map(item => ({
        label: item.energyName,
        value: item.id,
      })),
    }));
  }, [energyMediumSceneToMedium]);

  const { data, loading } = useRequest(
    () =>
      apiV2CustomerBillPagePost({
        ...searchParams,
        page: pageOffset,
        size: pageSize,
      }).then(res => {
        setTotalCount(res?.total ?? 0);
        return res.list || [];
      }),
    {
      refreshDeps: [pageOffset, pageSize, searchParams, update],
    }
  );
  const onReset = () => {
    setSearchParams({});
    setPageOffset(1);
  };
  const onFinish = (values: any) => {
    const searchParams = {
      ...values,
      energyMediumId: values.energyMediumId ? values.energyMediumId[values.energyMediumId.length - 1] : undefined,
      billMonth: values.billMonth ? dayjs(values.billMonth).startOf('month').valueOf() : undefined,
    };
    setSearchParams(searchParams);
    setPageOffset(1);
  };
  const deleteConfig = (id: number) => {
    apiV2CustomerBillDeletePost({ id }).then(() => {
      updateFn();
    });
  };
  const filters = (
    <CustomFilter form={form} onFinish={(val: any) => onFinish(val)} onReset={onReset}>
      <Form.Item name="billName" label="账单名称">
        <Input placeholder="请输入账单名称" allowClear />
      </Form.Item>
      <Form.Item name="billMonth" label="账单月份">
        <DatePicker picker="month" style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item name="energyMediumId" label="能源介质">
        <Cascader
          style={{ width: '100%' }}
          options={energyMediumSceneToMediumOptions}
          placeholder="请选择"
          allowClear
          showSearch={{
            filter: (inputValue: string, path: any[]) =>
              path.some(option => (option.label as string).toLowerCase().indexOf(inputValue.toLowerCase()) > -1),
          }}
        />
      </Form.Item>
    </CustomFilter>
  );
  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? [])]} filters={filters}>
      <Space size={8} style={{ marginBottom: 10 }}>
        <Button type="primary" onClick={() => navigate('/historybill/bills/import/create')}>
          新增账单
        </Button>
      </Space>
      <FieldTable rowKey="id" loading={loading} dataSource={data} columns={buildColumns} />
      <Paging pagingInfo={pagingInfo} />
    </Wrapper>
  );
};
export default HistoryBillImport;
